/**
 * The branded dialog. Every question the app asks goes through here instead of
 * window.confirm, window.prompt or window.alert, so "delete this recording?"
 * looks like Hooper Buddy and not like the browser.
 *
 * One element, built once on mount and reused for every ask. Questions that
 * arrive while a dialog is open wait in a queue, so two never stack up.
 */

let root = null;        // the backdrop element, appended to body once
let el = null;          // the cached inner nodes
let current = null;     // the ask on screen: { spec, resolve }
const queue = [];       // asks waiting their turn
let lastFocus = null;   // what had focus before we opened, so it gets it back
let downOnBackdrop = false;

// Input types the prompt accepts. Anything else falls back to text.
const TYPES = ['text', 'password', 'number', 'email', 'tel', 'url', 'search', 'date', 'time'];

export function mount() {
  // No ctx is needed here: the dialog asks a question and resolves, nothing else.
  if (root) return;
  root = document.createElement('div');
  root.className = 'hbdlg';
  root.innerHTML = `
    <div class="hbdlg-sheet" role="dialog" aria-modal="true" aria-labelledby="hbdlgTitle">
      <h2 id="hbdlgTitle"></h2>
      <p class="hbdlg-body" id="hbdlgBody"></p>
      <label class="hbdlg-field">
        <span class="hbdlg-lab"></span>
        <input class="hbdlg-input" type="text" autocomplete="off">
      </label>
      <div class="hbdlg-actions">
        <button type="button" class="btn hbdlg-cancel"></button>
        <button type="button" class="btn primary hbdlg-ok"></button>
      </div>
    </div>`;
  el = {
    sheet: root.querySelector('.hbdlg-sheet'),
    title: root.querySelector('#hbdlgTitle'),
    body: root.querySelector('#hbdlgBody'),
    field: root.querySelector('.hbdlg-field'),
    lab: root.querySelector('.hbdlg-lab'),
    input: root.querySelector('.hbdlg-input'),
    ok: root.querySelector('.hbdlg-ok'),
    cancel: root.querySelector('.hbdlg-cancel'),
  };
  el.ok.onclick = accept;
  el.cancel.onclick = cancel;
  // A press that starts AND ends on the backdrop cancels. Dragging a text
  // selection out of the sheet does not.
  root.addEventListener('mousedown', (e) => { downOnBackdrop = e.target === root; });
  root.addEventListener('click', (e) => { if (e.target === root && downOnBackdrop) cancel(); });
  // On document, in the capture phase, not on the sheet: clicking the title or
  // the body text moves focus to <body>, and a key pressed there never reaches
  // the sheet. The app's own document.onkeydown would then mark a shot or close
  // the session modal behind an open dialog. Capture gets us the key first and
  // lets us stop it before anything else in the page sees it.
  document.addEventListener('keydown', onKey, true);
  document.body.appendChild(root);
}

/** -> Promise<boolean> */
export function confirm(spec = {}) { return ask({ ...spec, kind: 'confirm' }); }

/** -> Promise<string|null> (null when cancelled) */
export function prompt(spec = {}) { return ask({ ...spec, kind: 'prompt' }); }

/** -> Promise<void>, resolves once it is dismissed */
export function alert(spec = {}) { return ask({ ...spec, kind: 'alert' }); }

function ask(spec) {
  mount();                       // safe to call every time, it builds once
  // Catch the opener here rather than in show(): between two queued asks the
  // sheet blinks shut and focus lands on <body>, which is not where the player
  // was. Only the ask that starts a chain has the real opener to go back to.
  if (!current) lastFocus = document.activeElement;
  return new Promise((resolve) => {
    queue.push({ spec, resolve });
    if (!current) next();
  });
}

function next() {
  current = queue.shift() || null;
  if (current) show(current.spec);
}

function show(spec) {
  const kind = spec.kind;
  const danger = kind === 'confirm' && !!spec.danger;

  el.title.textContent = spec.title || (kind === 'alert' ? 'Heads up' : 'Are you sure?');
  el.body.textContent = spec.body || '';
  el.body.hidden = !spec.body;
  if (spec.body) el.sheet.setAttribute('aria-describedby', 'hbdlgBody');
  else el.sheet.removeAttribute('aria-describedby');

  el.field.hidden = kind !== 'prompt';
  el.lab.textContent = spec.label || '';
  el.lab.hidden = !spec.label;
  el.input.type = TYPES.includes(spec.type) ? spec.type : 'text';
  el.input.value = spec.value == null ? '' : String(spec.value);
  el.input.placeholder = spec.placeholder || '';

  el.ok.textContent = spec.confirmText || (kind === 'prompt' ? 'Save' : kind === 'alert' ? 'Got it' : 'Yes');
  el.ok.className = `btn primary hbdlg-ok${danger ? ' hbdlg-danger' : ''}`;
  el.cancel.textContent = spec.cancelText || 'Cancel';
  el.cancel.hidden = kind === 'alert';
  el.sheet.classList.toggle('danger', danger);

  root.classList.add('open');
  // preventScroll so the page behind us does not jump to the sheet.
  if (kind === 'prompt') { el.input.focus({ preventScroll: true }); el.input.select(); }
  else el.ok.focus({ preventScroll: true });
}

function settle(value) {
  if (!current) return;
  const { resolve } = current;
  current = null;
  root.classList.remove('open');
  el.sheet.classList.remove('danger');
  // Give focus back only when nothing is waiting, so a queue does not flicker.
  if (!queue.length && lastFocus && document.contains(lastFocus)) lastFocus.focus?.();
  resolve(value);
  if (queue.length) next();
}

function accept() {
  if (!current) return;
  const kind = current.spec.kind;
  if (kind === 'prompt') settle(el.input.value.trim());
  else if (kind === 'confirm') settle(true);
  else settle(undefined);
}

function cancel() {
  if (!current) return;
  const kind = current.spec.kind;
  if (kind === 'prompt') settle(null);
  else if (kind === 'confirm') settle(false);
  else settle(undefined);
}

function onKey(e) {
  if (!current) return;
  // The app binds its own document.onkeydown (Escape closes the session modal,
  // m and n mark shots). While we are open none of that should fire.
  e.stopPropagation();
  if (e.key === 'Escape') { e.preventDefault(); cancel(); return; }
  if (e.key === 'Enter' && !e.shiftKey) {
    if (document.activeElement === el.cancel) return;  // Enter on Cancel means cancel
    e.preventDefault(); accept(); return;
  }
  if (e.key === 'Tab') trap(e);
}

// Keep Tab inside the sheet. offsetParent is null for anything hidden.
function trap(e) {
  const items = [el.input, el.cancel, el.ok].filter((n) => n.offsetParent);
  if (!items.length) return;
  const i = items.indexOf(document.activeElement);
  const to = e.shiftKey
    ? (i <= 0 ? items.length - 1 : i - 1)
    : (i === items.length - 1 ? 0 : i + 1);
  e.preventDefault();
  items[to].focus();
}
